import { useState } from 'react';
import type { DateSlot } from '../../poller/types.js';
import { REGIONS } from '../../regions.js';
import type { SearchResult } from '../client.ts';
import { FORMAT_SLOTS, formatLabel } from '../lib/formats.js';
import { FormatIcon } from './FormatIcon.tsx';

/**
 * Formats and region, before it goes on the list.
 *
 * Everything here has a default that means "don't care", so the panel can be
 * ignored entirely — which is the point. Picking nothing is picking all.
 */
export function Options({
  result,
  busy,
  onAdd,
  onCancel,
}: {
  result: SearchResult;
  busy: boolean;
  onAdd: (formats: DateSlot[], region: string | null) => void;
  onCancel: () => void;
}) {
  const [formats, setFormats] = useState<DateSlot[]>([]);
  const [region, setRegion] = useState<string>('');

  // A person and a series are not published in formats — the books under them
  // are, each with their own. Asking here would be asking about nothing.
  const choosable = result.kind === 'title' ? FORMAT_SLOTS[result.medium] : [];

  function toggle(slot: DateSlot) {
    setFormats((current) =>
      current.includes(slot) ? current.filter((s) => s !== slot) : [...current, slot],
    );
  }

  return (
    <div className="options">
      {choosable.length > 0 && (
        <>
          <p className="options-label">Tell me when it&apos;s out as</p>
          <div className="picks">
            {choosable.map((slot) => {
              const on = formats.includes(slot);
              return (
                <button
                  key={slot}
                  type="button"
                  className={on ? 'pick on' : 'pick'}
                  aria-pressed={on}
                  onClick={() => toggle(slot)}
                >
                  <FormatIcon slot={slot} medium={result.medium} size={15} />
                  {formatLabel(slot, result.medium)}
                </button>
              );
            })}
          </div>
          <p className="options-note">
            {formats.length === 0
              ? 'Any of them — the first one to arrive counts.'
              : 'Only these will move it to “out now”. The rest still show on its page.'}
          </p>
        </>
      )}

      <p className="options-label">Region</p>
      <select
        className="field"
        value={region}
        onChange={(e) => setRegion(e.target.value)}
        aria-label="Region"
      >
        <option value="">Wherever it lands first</option>
        {REGIONS.map((r) => (
          <option key={r.code} value={r.code}>
            {r.name}
          </option>
        ))}
      </select>
      <p className="options-note">
        {result.medium === 'book'
          ? // Said plainly rather than implied by a control that does nothing:
            // no catalogue we can read knows which region an audiobook is sold
            // in, and pretending otherwise is the version of this that lies.
            'Saved with it, but book dates aren’t published by region — nothing we read knows which region an audiobook is sold in.'
          : 'Release dates differ by country; this picks whose to show.'}
      </p>

      <div className="options-actions">
        <button type="button" className="more" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="primary-btn"
          disabled={busy}
          onClick={() => onAdd(formats, region || null)}
        >
          {busy ? 'Adding…' : 'Add to slate'}
        </button>
      </div>
    </div>
  );
}
